import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, Zap, AlertOctagon } from "lucide-react";

function fmtRel(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 5) return "ahora";
    if (diff < 60) return `hace ${Math.floor(diff)}s`;
    if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
    return d.toLocaleTimeString("es-ES");
  } catch { return iso; }
}

export default function RetryWorkerBar({ onChanged }) {
  const [s, setS] = useState(null);
  const [ticking, setTicking] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/retry-worker");
      setS(data);
    } catch (e) {
      console.error("RetryWorker status load failed", e);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const onTick = async () => {
    setTicking(true);
    try {
      const { data } = await api.post("/admin/retry-worker/tick");
      toast.success(`Tick ejecutado: ${data.processed} transacciones procesadas`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setTicking(false); }
  };

  if (!s) return null;
  const running = s.running && s.enabled;
  const dotColor = running ? "bg-emerald-500" : "bg-slate-400";

  return (
    <div
      data-testid="retry-worker-bar"
      className="bg-white border border-slate-200 rounded-md p-4 mb-6 flex flex-col lg:flex-row gap-3 lg:items-center justify-between"
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${dotColor} ${running ? "animate-pulse" : ""}`} />
          <Activity className="w-3.5 h-3.5 text-slate-700" />
          <span className="font-bold tracking-wide uppercase">Worker reintentos</span>
          <span className={`font-mono ${running ? "text-emerald-700" : "text-slate-500"}`} data-testid="retry-worker-state">
            {s.enabled ? (s.running ? "activo" : "parado") : "deshabilitado"}
          </span>
        </div>
        <Metric label="intervalo" value={`${s.interval_seconds}s`} />
        <Metric label="max intentos" value={s.max_attempts} />
        <Metric label="backoff" value={`${s.backoff_base_seconds}s..${s.backoff_cap_seconds}s`} />
        <Metric label="último tick" value={fmtRel(s.last_run_at)} testId="retry-worker-last-run" />
        <Metric label="elegibles ahora" value={s.eligible_now ?? 0} testId="retry-worker-eligible" />
        <Metric label="éxitos" value={s.total_successes ?? 0} />
        <Metric
          label="dead-letter"
          value={
            <span className="inline-flex items-center gap-1">
              {(s.dead_letter_count ?? 0) > 0 && <AlertOctagon className="w-3 h-3 text-red-600" />}
              <span className={(s.dead_letter_count ?? 0) > 0 ? "text-red-700 font-bold" : ""}>
                {s.dead_letter_count ?? 0}
              </span>
            </span>
          }
          testId="retry-worker-deadletter"
        />
      </div>
      <Button
        size="sm"
        variant="outline"
        className="h-8 text-xs"
        onClick={onTick}
        disabled={ticking}
        data-testid="retry-worker-tick-button"
      >
        <Zap className={`w-3.5 h-3.5 mr-1 ${ticking ? "animate-pulse" : ""}`} />
        {ticking ? "Procesando…" : "Forzar tick ahora"}
      </Button>
    </div>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div className="flex items-baseline gap-1.5" data-testid={testId}>
      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</span>
      <span className="font-mono font-semibold text-slate-900">{value}</span>
    </div>
  );
}
